import { TextArea } from "components/inputs";
import type {
  Control,
  FieldErrors,
  FieldValues,
  Path,
  RegisterOptions,
} from "react-hook-form";
import { Controller } from "react-hook-form";
import { useCallback } from "react";

type ControlledTextAreaProps<T extends FieldValues> = {
  control: Control<T>;
  name: Path<T>;
  label?: string | null;
  errors: FieldErrors<T>;
  onSave: (value: string | number) => Promise<void>;
  readOnly?: boolean;
  maxLength?: number;
  advancedEdit?: boolean;
  allowMinimize?: boolean;
  classNames?: string;
  rules?: RegisterOptions<T, Path<T>>;
  placeholder?: string;
};

/*
  we use ControlledTextArea when we want to save the value of the text area to the usual KAboodle form state
*/

export const ControlledTextArea = <T extends FieldValues>({
  control,
  name,
  label,
  errors = {},
  onSave,
  readOnly = false,
  advancedEdit = false,
  allowMinimize = false,
  classNames,
  rules,
  placeholder,
}: ControlledTextAreaProps<T>) => {
  // wrap onSave in useCallback to prevent unnecessary rerenders
  const handleSave = useCallback(
    async (value: string | number) => {
      try {
        await onSave(value);
      } catch (error) {
        console.error(`Error saving field ${name}:`, error);
      }
    },
    [onSave, name],
  );

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field }) => (
        <TextArea
          {...field}
          classNames={classNames}
          label={label ?? ""}
          errors={errors}
          readOnly={readOnly}
          advancedEdit={advancedEdit}
          allowMinimize={allowMinimize}
          placeholder={placeholder}
          onSave={handleSave}
        />
      )}
    />
  );
};
